import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import DeviceMockup from "./DeviceMockup";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HeroBackground: React.FC = () => {
  const { scrollY } = useScroll();
  const circleY1 = useTransform(scrollY, [0, 500], [0, 60]);
  const circleY2 = useTransform(scrollY, [0, 500], [0, -40]);

  return (
    <div className="wred-hero__bg" aria-hidden="true">
      <div className="wred-hero__radial" />
      <svg className="wred-hero__lines" viewBox="0 0 1440 900" preserveAspectRatio="none">
        <path
          d="M0 420 C320 280, 520 560, 900 400 S1440 320, 1440 480"
          fill="none"
          stroke="url(#wredLineGrad)"
          strokeWidth="1.2"
          opacity="0.35"
        />
        <path
          d="M0 620 C280 500, 640 720, 1100 560 S1440 680, 1440 760"
          fill="none"
          stroke="url(#wredLineGrad)"
          strokeWidth="1"
          opacity="0.22"
        />
        <defs>
          <linearGradient id="wredLineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2563eb" stopOpacity="0" />
            <stop offset="45%" stopColor="#2563eb" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#ff8c00" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      <motion.span className="wred-hero__orb wred-hero__orb--1" style={{ y: circleY1 }} />
      <motion.span className="wred-hero__orb wred-hero__orb--2" style={{ y: circleY2 }} />
      <span className="wred-hero__orb wred-hero__orb--3" />
    </div>
  );
};

const WredHero: React.FC = () => (
  <section className="wred-hero" aria-labelledby="wredesign-hero-title">
    <HeroBackground />

    <div className="wredesign-container wred-hero__inner">
      <div className="wred-hero__grid">
        <div className="wred-hero__content">
          <motion.span
            className="wred-hero__badge"
            custom={0.08}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            Website Redesigning
          </motion.span>

          <motion.h1
            id="wredesign-hero-title"
            custom={0.16}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            Website Redesigning <span>Services</span>
          </motion.h1>

          <motion.p
            className="wred-hero__desc"
            custom={0.24}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            Transform your outdated website into a modern, high-performing digital experience
            that drives conversions and business growth.
          </motion.p>

          <motion.div
            className="wred-hero__actions"
            custom={0.32}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }}>
              <Link className="wred-hero__btn wred-hero__btn--primary" to="/contact">
                <span>Get Free Consultation</span>
                <span className="wred-hero__btn-icon" aria-hidden="true">
                  <ArrowRight size={18} strokeWidth={2} />
                </span>
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <div className="wred-hero__visual">
          <DeviceMockup />
        </div>
      </div>
    </div>
  </section>
);

export default WredHero;
